import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { VideoApi } from '@/lib/api/videoApi';
import { API_CONFIG } from '@/config/api';
import React from 'react';

// Cache keys
export const VIDEO_CACHE_KEYS = {
  all: ['videos'] as const,
  lists: () => [...VIDEO_CACHE_KEYS.all, 'list'] as const,
  list: (filters: { page: number; pageSize: number }) => [...VIDEO_CACHE_KEYS.lists(), filters] as const,
  details: () => [...VIDEO_CACHE_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...VIDEO_CACHE_KEYS.details(), id] as const,
  status: (id: string) => [...VIDEO_CACHE_KEYS.all, 'status', id] as const,
} as const;

// Video API instance
const videoApi = new VideoApi();

// Helper to check if signed URL is expired (1 hour expiration)
const isUrlExpired = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const expiresParam = urlObj.searchParams.get('X-Goog-Expires');
    if (!expiresParam) return true;
    
    const expiresIn = parseInt(expiresParam);
    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime >= expiresIn;
  } catch {
    return true;
  }
};

// Custom hook for video list with caching
export const useVideoList = (page: number = 1, pageSize: number = 20) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: VIDEO_CACHE_KEYS.list({ page, pageSize }),
    queryFn: async () => {
      if (!session?.access_token) throw new Error('No access token');
      console.log(`[useVideoList] Fetching videos page ${page}, size ${pageSize}`);
      return videoApi.listVideos(session.access_token, page, pageSize);
    },
    enabled: !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes - longer stale time to reduce API calls
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on component mount if data exists
    refetchOnReconnect: false, // Prevent refetch on reconnect
    retry: 2, // Reduce retry attempts
    retryDelay: 1000, // 1 second delay between retries
    select: (videos) => {
      // Store basic video info in cache without triggering individual fetches
      videos.forEach(video => {
        // Pre-populate individual video cache with basic info
        queryClient.setQueryData(VIDEO_CACHE_KEYS.detail(video.id), {
          ...video,
          // Don't include signed URLs in basic cache - fetch them separately when needed
          playback_url: undefined,
          thumbnail_url: undefined
        });
      });
      return videos;
    },
  });
};

// Custom hook for individual video with caching
export const useVideo = (id: string) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: VIDEO_CACHE_KEYS.detail(id),
    queryFn: async () => {
      if (!session?.access_token) throw new Error('No access token');
      console.log(`[useVideo] Fetching video ${id}`);
      return videoApi.getVideo(id, session.access_token);
    },
    enabled: !!session?.access_token && !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes - longer stale time for individual videos
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on component mount if data exists
    refetchOnReconnect: false, // Prevent refetch on reconnect
    retry: 1, // Reduce retry attempts
    retryDelay: 2000, // 2 second delay between retries
    select: (video) => {
      // Don't trigger recursive fetches in select - this can cause infinite loops
      return video;
    },
  });
};

// Custom hook for video status with caching
export const useVideoStatus = (id: string) => {
  const { session } = useAuth();

  return useQuery({
    queryKey: VIDEO_CACHE_KEYS.status(id),
    queryFn: async () => {
      if (!session?.access_token) throw new Error('No access token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/videos/${id}/status`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch video status');
      return response.json();
    },
    enabled: !!session?.access_token && !!id,
    refetchInterval: 5000, // Refetch every 5 seconds for status updates
    staleTime: 0, // Always fetch fresh status
  });
};

// Custom hook for video upload with cache invalidation
export const useVideoUpload = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, metadata, processingOptions }: {
      file: File;
      metadata: { title: string; description: string; file_name: string; file_size: number };
      processingOptions?: { generate_transcript?: boolean; detect_speakers?: boolean; extract_objects?: boolean };
    }) => {
      if (!session?.access_token) throw new Error('No access token');
      return videoApi.uploadVideo(file, metadata, session.access_token, processingOptions);
    },
    onSuccess: (newVideo) => {
      // Invalidate and refetch video lists
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.lists() });
      
      // Add the new video to cache
      queryClient.setQueryData(VIDEO_CACHE_KEYS.detail(newVideo.id), newVideo);
      
      // Prefetch video status
      queryClient.prefetchQuery({
        queryKey: VIDEO_CACHE_KEYS.status(newVideo.id),
        queryFn: async () => {
          const response = await fetch(`${API_CONFIG.BASE_URL}/videos/${newVideo.id}/status`, {
            headers: {
              'Authorization': `Bearer ${session!.access_token}`,
              'Accept': 'application/json',
            },
          });
          return response.json();
        },
      });
    },
  });
};

// Custom hook for video processing with cache invalidation
export const useVideoProcessing = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ videoId, processingOptions }: {
      videoId: string;
      processingOptions: { generate_transcript?: boolean; detect_speakers?: boolean; extract_objects?: boolean };
    }) => {
      if (!session?.access_token) throw new Error('No access token');
      return videoApi.processVideo(videoId, processingOptions, session.access_token);
    },
    onSuccess: (_, { videoId }) => {
      // Invalidate video status and details
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.status(videoId) });
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.detail(videoId) });
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.lists() });
    },
  });
};

// Custom hook for video retry with cache invalidation
export const useVideoRetry = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (videoId: string) => {
      if (!session?.access_token) throw new Error('No access token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/videos/${videoId}/retry-processing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to retry video processing');
      return response.json();
    },
    onSuccess: (_, videoId) => {
      // Invalidate video status and details
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.status(videoId) });
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.detail(videoId) });
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.lists() });
    },
  });
};

// Custom hook for video search indexing with cache invalidation
export const useVideoSearchIndexing = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (videoId: string) => {
      if (!session?.access_token) throw new Error('No access token');
      const response = await fetch(`${API_CONFIG.BASE_URL}/videos/${videoId}/index-for-search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to index video for search');
      return response.json();
    },
    onSuccess: (_, videoId) => {
      // Invalidate video status and details
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.status(videoId) });
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.detail(videoId) });
      queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.lists() });
    },
  });
};

// Hook for refreshing expired URLs
export const useVideoUrlRefresh = (videoId: string, playbackUrl?: string) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (playbackUrl && isUrlExpired(playbackUrl) && session?.access_token) {
      // Only refresh if URL is expired
      queryClient.prefetchQuery({
        queryKey: VIDEO_CACHE_KEYS.detail(videoId),
        queryFn: () => videoApi.getVideo(videoId, session.access_token),
        staleTime: 0,
      });
    }
  }, [playbackUrl, videoId, session?.access_token, queryClient]);

  return null; // This hook doesn't return anything, it just manages URL refresh
};

// Hook for getting signed URLs only when needed
export const useVideoSignedUrls = (videoId: string) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Check if we need to fetch signed URLs
  const needsSignedUrls = React.useMemo(() => {
    if (!session?.access_token || !videoId) return false;
    
    const cachedVideo = queryClient.getQueryData(VIDEO_CACHE_KEYS.detail(videoId)) as { playback_url?: string } | undefined;
    if (!cachedVideo || !cachedVideo.playback_url) return true;
    
    return isUrlExpired(cachedVideo.playback_url);
  }, [session?.access_token, videoId, queryClient]);

  return useQuery({
    queryKey: [...VIDEO_CACHE_KEYS.detail(videoId), 'signed-urls'],
    queryFn: async () => {
      if (!session?.access_token) throw new Error('No access token');
      console.log(`[useVideoSignedUrls] Fetching signed URLs for video ${videoId}`);
      return videoApi.getVideo(videoId, session.access_token);
    },
    enabled: needsSignedUrls,
    staleTime: 50 * 60 * 1000, // 50 minutes (just under 1 hour expiration)
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
    retryDelay: 1000,
  });
};

// Utility hook for cache management
export const useVideoCacheManager = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const clearAllVideoCache = () => {
    queryClient.removeQueries({ queryKey: VIDEO_CACHE_KEYS.all });
  };

  const refreshAllVideos = () => {
    queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.all });
  };

  const refreshVideo = (id: string) => {
    queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.detail(id) });
    queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.status(id) });
  };

  const refreshVideoList = () => {
    queryClient.invalidateQueries({ queryKey: VIDEO_CACHE_KEYS.lists() });
  };

  const refreshSignedUrls = (id: string) => {
    queryClient.invalidateQueries({ 
      queryKey: [...VIDEO_CACHE_KEYS.detail(id), 'signed-urls'] 
    });
  };

  const clearExpiredUrls = () => {
    // Clear all signed URL caches to force refresh
    queryClient.removeQueries({ 
      queryKey: VIDEO_CACHE_KEYS.all,
      predicate: (query) => query.queryKey.includes('signed-urls')
    });
  };

  // Clear cache when user changes
  React.useEffect(() => {
    if (session?.access_token) {
      // Clear cache on new login
      clearAllVideoCache();
    }
  }, [session?.access_token]);

  return {
    clearAllVideoCache,
    refreshAllVideos,
    refreshVideo,
    refreshVideoList,
    refreshSignedUrls,
    clearExpiredUrls,
  };
}; 